/**
 * The section menu: a burger, and every card on the page behind it.
 *
 * The page is eleven thousand pixels of one continuous scroll. There is no
 * second page to navigate to, so a conventional site menu would be a list of
 * one — what a reader actually needs here is to see the shape of the thing and
 * jump inside it.
 *
 * ## Built from the DOM, not from the data
 *
 * The menu reads `[data-skin-anchor]` after the blocks have rendered. Building
 * it from the page means it cannot disagree with the page: a card added,
 * removed or renamed in the editor turns up in the menu on the next load with
 * nothing here to keep in step. The alternative — a second list of sections —
 * is a list that goes stale the first time someone edits a title.
 *
 * ## Not part of the motion layer
 *
 * `motion.init` is the only hook a skin gets on the rendered root, so this is
 * mounted from there, but it is mounted *before* the reduced-motion return.
 * Navigation is not motion. A reader who has asked for less movement still
 * needs to get around, and the contract asks motion to respect that preference,
 * not to withhold the page's only means of navigation from them.
 */

import { claimAnchor } from './anchors'

/** How the menu scrolls. Lenis owns the scroll when the motion layer is up, and
 *  a plain `scrollIntoView` fights it; motion hands us its scroller instead. */
type Scroller = (target: HTMLElement) => void
let scroller: Scroller | null = null

export function setScroller(fn: Scroller | null): void {
  scroller = fn
}

const REDUCED = (): boolean => window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * Anchor the two headings the block renderers never see.
 *
 * The cards claim their own ids as they render, but the hero and the heading
 * that opens the method are drawn by core's showcase layout, so nothing in
 * `skin/blocks/` ever touches them. Without these the menu can carry a reader
 * into the middle of the page and offer them no way back to the top, which on
 * eleven thousand pixels of scroll is the one jump they are most likely to
 * want.
 *
 * Claimed here rather than in a block renderer because this is the only code
 * that runs against the whole rendered root. They are absent from the
 * server-rendered body, which is not a parity break: core renders these two,
 * `prerender.py` does not, so there is no server-side id for them to disagree
 * with, and `settle()` below re-asserts a deep link once they exist.
 */
function claimLayoutAnchors(root: HTMLElement): void {
  const hero = root.querySelector<HTMLElement>('.rem-showcase__hero')
  const title = hero?.querySelector<HTMLElement>('h1')
  if (hero && title && !hero.dataset.skinAnchor) {
    claimAnchor(hero, title.textContent?.trim() || 'Top', 'Top')
  }

  // The heading, not the article that contains it. The article runs the whole
  // length of the method, so marking it current would light "The method" up
  // beside every card in turn; a heading is thin enough to be current only
  // while it is actually on screen.
  const heading = root.querySelector<HTMLElement>('.rem-showcase__article > h2')
  if (heading && !heading.dataset.skinAnchor) {
    claimAnchor(heading, heading.textContent?.trim() || 'The method', 'The method')
  }
}

/**
 * Lift the menu clear of the page chrome above it.
 *
 * The menu sits top-right. Core's header — the wordmark and the "All projects"
 * link — occupies that corner for the first 61 pixels of scroll and then
 * scrolls away, so a fixed offset either collides with it at the top of the
 * page or floats stranded below empty space everywhere else. The lift is the
 * header's still-visible remainder, measured every scrolled frame, so the
 * button rides the header off the screen and then holds the corner.
 *
 * Measured rather than assumed, for the same reason the old bottom-right
 * placement measured the platform's edit bar: the chrome belongs to core, its
 * height is not this skin's business, and a hard-coded number goes stale
 * silently. The header is re-queried on every pass because core swaps the
 * prerendered body out after hydration and the element this saw first no
 * longer exists afterwards.
 */
function liftAboveChrome(nav: HTMLElement): () => void {
  let queued = false
  const apply = (): void => {
    queued = false
    const header = document.querySelector<HTMLElement>('.rem-header')
    const bottom = header?.getBoundingClientRect().bottom ?? 0
    nav.style.setProperty('--skin-nav-lift', `${Math.max(0, Math.round(bottom))}px`)
  }
  const schedule = (): void => {
    if (queued) return
    queued = true
    requestAnimationFrame(apply)
  }
  apply()
  window.addEventListener('scroll', schedule, { passive: true })
  window.addEventListener('resize', schedule, { passive: true })
  return () => {
    window.removeEventListener('scroll', schedule)
    window.removeEventListener('resize', schedule)
  }
}

export function mountNav(root: HTMLElement): () => void {
  claimLayoutAnchors(root)
  const targets = Array.from(root.querySelectorAll<HTMLElement>('[data-skin-anchor]'))
  // One card is not a menu. Below a handful the page is short enough to read
  // straight through, and a burger over it is furniture for its own sake.
  if (targets.length < 4) return () => undefined

  const nav = document.createElement('nav')
  nav.className = 'skin-nav'
  nav.setAttribute('aria-label', 'Sections')

  const toggle = document.createElement('button')
  toggle.type = 'button'
  toggle.className = 'skin-nav__toggle'
  toggle.setAttribute('aria-expanded', 'false')
  toggle.setAttribute('aria-controls', 'skin-nav-panel')
  // The bars are markup, not an icon set: the brand guideline rules those out,
  // and three spans that animate into a cross cost nothing.
  for (let i = 0; i < 3; i += 1) {
    const bar = document.createElement('span')
    bar.className = 'skin-nav__bar'
    bar.setAttribute('aria-hidden', 'true')
    toggle.append(bar)
  }
  const label = document.createElement('span')
  label.className = 'skin-nav__label'
  label.textContent = 'Sections'
  toggle.append(label)

  const panel = document.createElement('div')
  panel.id = 'skin-nav-panel'
  panel.className = 'skin-nav__panel'
  // Lenis owns the wheel for the page's smooth scroll, so without this a
  // wheel over the open panel scrolled the article behind it — the one
  // element on the page with its own scrollbar was the one the wheel could
  // not reach. The attribute is Lenis's own opt-out.
  panel.setAttribute('data-lenis-prevent', '')
  panel.hidden = true

  const list = document.createElement('ol')
  list.className = 'skin-nav__list'

  const links = new Map<string, HTMLAnchorElement>()
  for (const target of targets) {
    const item = document.createElement('li')
    const link = document.createElement('a')
    link.href = `#${target.id}`
    link.className = 'skin-nav__link'

    const eyebrow = target.dataset.skinAnchorEyebrow
    if (eyebrow) {
      const tag = document.createElement('span')
      tag.className = 'skin-nav__eyebrow'
      tag.textContent = eyebrow
      link.append(tag)
    }
    const text = document.createElement('span')
    text.className = 'skin-nav__text'
    text.textContent = target.dataset.skinAnchor ?? target.id
    link.append(text)

    link.addEventListener('click', (event) => {
      // Handled here so the scroll goes through Lenis when it is running, and
      // so the address bar still ends up with the fragment in it — a menu you
      // cannot copy a link out of is half a menu.
      event.preventDefault()
      close()
      // Long enough to cover a smooth scroll the length of the page. The
      // cards crossed on the way are not where the reader asked to be.
      quietUntil = Date.now() + 1400
      history.pushState(null, '', `#${target.id}`)
      if (scroller) scroller(target)
      else target.scrollIntoView({ behavior: REDUCED() ? 'auto' : 'smooth', block: 'start' })
      // Move the reading position, not just the viewport. Without this the
      // next Tab press carries on from the menu button.
      target.setAttribute('tabindex', '-1')
      target.focus({ preventScroll: true })
    })

    links.set(target.id, link)
    item.append(link)
    list.append(item)
  }

  panel.append(list)
  nav.append(toggle, panel)
  document.body.append(nav)
  const unlift = liftAboveChrome(nav)

  let open = false
  function setOpen(next: boolean): void {
    open = next
    toggle.setAttribute('aria-expanded', String(open))
    nav.classList.toggle('is-open', open)
    panel.hidden = !open
    if (open) {
      const first = panel.querySelector<HTMLAnchorElement>('a')
      first?.focus()
    }
  }
  function close(): void {
    if (open) setOpen(false)
  }

  toggle.addEventListener('click', () => {
    setOpen(!open)
    if (!open) toggle.focus()
  })

  const onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && open) {
      setOpen(false)
      toggle.focus()
    }
  }
  const onPointer = (event: MouseEvent): void => {
    if (open && !nav.contains(event.target as Node)) close()
  }
  document.addEventListener('keydown', onKey)
  document.addEventListener('pointerdown', onPointer)

  // Mark where the reader is, in the menu and in the address bar.
  //
  // ## Why a band across the middle, and not the whole viewport
  //
  // An observer only fires when an element crosses a threshold. Watching the
  // whole viewport, six cards are on screen at once and scrolling from the
  // first to the last crosses nothing — no callback fires and the current
  // section goes stale exactly while the reader is moving. A band 10% tall
  // across the middle turns "which card is the reader on" into an entry and an
  // exit, which is a thing an observer can see.
  //
  // ## Why the address bar is written here
  //
  // A reader who scrolls to a card and copies the URL should get that card.
  // `replaceState`, never `push`: eighteen sections pushed onto the history
  // stack would mean eighteen presses of Back to leave the page.
  const visible = new Set<HTMLElement>()
  let current: HTMLElement | null = null
  // Set while the page is scrolling itself — a menu jump or a deep link. The
  // cards crossed on the way are passed through, not arrived at, and writing
  // each one to the address bar would leave whichever we passed last.
  let quietUntil = 0

  /** Distance from the middle of the viewport to the nearest edge of `el`,
   *  and zero while the middle is inside it. */
  function fromCentre(el: HTMLElement): number {
    const middle = window.innerHeight / 2
    const box = el.getBoundingClientRect()
    if (box.top <= middle && box.bottom >= middle) return 0
    return box.top > middle ? box.top - middle : middle - box.bottom
  }

  function markCurrent(): void {
    // Two cards can be in the band at once. The one the middle of the screen
    // is actually inside wins; document order breaks a genuine tie, so the
    // menu reads down the page rather than jumping about.
    let best: HTMLElement | null = null
    let bestDistance = Number.POSITIVE_INFINITY
    for (const target of targets) {
      if (!visible.has(target)) continue
      const distance = fromCentre(target)
      if (distance < bestDistance) {
        bestDistance = distance
        best = target
      }
    }
    // Nothing in the band happens at the very end of the page, where the
    // footer holds the middle. Keeping the last section beats clearing it:
    // the reader has not left it, the page has simply run out.
    if (!best) return

    if (best !== current) {
      if (current) links.get(current.id)?.classList.remove('is-here')
      links.get(best.id)?.classList.add('is-here')
      current = best
    }

    // The address bar is a separate question from the marker, and is asked on
    // every pass rather than only when the section changes: the reader can
    // leave the first screen without the current section changing, and that
    // alone has to put the fragment back.
    if (Date.now() < quietUntil) return
    // Inside the first screen the reader has not navigated anywhere yet, so
    // the honest thing to copy out of the address bar is the page itself. A
    // page that rewrote its own URL to a fragment the moment it loaded would
    // hand out a link nobody chose.
    const atTop = window.scrollY < window.innerHeight * 0.5
    const wantHash = atTop ? '' : `#${best.id}`
    if (location.hash !== wantHash) {
      history.replaceState(null, '', wantHash || location.pathname + location.search)
    }
  }

  const here = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) visible.add(entry.target as HTMLElement)
        else visible.delete(entry.target as HTMLElement)
      }
      markCurrent()
    },
    { rootMargin: '-45% 0px -45% 0px' },
  )
  for (const target of targets) here.observe(target)

  // The observer only speaks when something crosses the band. Between those
  // crossings the reader can still change which card holds the middle — two
  // short cards inside the band at once — so the choice is re-made on scroll,
  // cheaply, off a frame that is happening anyway.
  let queued = false
  const onScroll = (): void => {
    if (queued) return
    queued = true
    requestAnimationFrame(() => {
      queued = false
      markCurrent()
    })
  }
  window.addEventListener('scroll', onScroll, { passive: true })

  // Re-honour a deep link once the cards exist.
  //
  // The browser follows `#…` against the server-rendered body on first paint,
  // and then core replaces that body with the rendered blocks — which throws
  // the scroll position away and rebuilds the element somewhere else. Landing
  // at the top of a page someone linked into the middle of is the whole
  // failure this menu was added to fix, so the jump is repeated here, once,
  // after the anchors are real.
  //
  // Instantly, not smoothly: the reader asked to start there. Animating down
  // eleven thousand pixels to a place they already chose is theatre.
  const wanted = decodeURIComponent(location.hash.slice(1))
  const target = wanted ? targets.find((el) => el.id === wanted) : undefined
  if (target) {
    // Repeated, not fired once, because one attempt does not survive what
    // happens next: Lenis takes the scroll over and starts it at zero, media
    // above the target loads and changes its offset, and ScrollTrigger's first
    // refresh restores a position of its own. Each of those lands the reader
    // somewhere other than where they asked to be.
    //
    // So it re-asserts until the target is actually near the top, and gives up
    // the moment the reader scrolls — chasing a position someone has just
    // scrolled away from is the worse failure of the two.
    let surrendered = false
    const yieldToReader = (): void => {
      surrendered = true
    }
    for (const event of ['wheel', 'touchstart', 'keydown'] as const) {
      window.addEventListener(event, yieldToReader, { once: true, passive: true })
    }
    let tries = 0
    const settle = (): void => {
      if (surrendered || tries > 8) {
        for (const event of ['wheel', 'touchstart', 'keydown'] as const) {
          window.removeEventListener(event, yieldToReader)
        }
        return
      }
      tries += 1
      quietUntil = Date.now() + 400
      const top = target.getBoundingClientRect().top
      if (Math.abs(top) > 8) target.scrollIntoView({ behavior: 'auto', block: 'start' })
      window.setTimeout(settle, 120)
    }
    requestAnimationFrame(settle)
  }

  return () => {
    document.removeEventListener('keydown', onKey)
    document.removeEventListener('pointerdown', onPointer)
    window.removeEventListener('scroll', onScroll)
    here.disconnect()
    unlift()
    nav.remove()
  }
}
